import type { ComputedRef } from 'vue';
import type { DsBreakpoint } from '~/composables/ds/useDsBreakpoint';
import { useIsPopup } from '~/composables/popup/usePopupCallback';
// Import explicite : un composable ajouté après le dernier scan d'auto-imports n'est pas résolu
// sous vitest (cf. les autres imports explicites de ce dossier).
import { useScreenVersion } from '~/composables/ds/useScreenVersion';

// Décision « on monte le shell mobile ? » centralisée : viewport mobile ET feature `mobile`
// ouverte (donc sous kill-switch) ET hors popup — layouts/default.vue rend la branche popup AVANT
// celle du shell, une popup n'a donc pas de `.gm-scroll` et doit garder son scroll de page.
//
// Même motif d'extraction que useChromeVersion : la condition était dupliquée dans
// layouts/default.vue et pages/opportunities/[opportunityHashId]/simulation/auto.vue, et
// simulation.vue en a besoin à son tour (la sous-nav desktop doit s'effacer sous le shell) —
// 3ᵉ répétition, on extrait.
export interface MobileShell {
  showMobileShell: ComputedRef<boolean>;
  // Contrat hauteur/scroll d'un wrapper de page, porté ICI et pas redérivé par chaque page :
  // sous le shell, c'est `.gm-scroll` (dans MobileApp) qui scrolle et qui donne la hauteur. Une
  // page qui garde son propre `h-full overflow-auto` empile un second conteneur scrollable de
  // hauteur contrainte dans le premier — deux ascenseurs, et un écran qui ne défile plus d'un
  // bloc. Le défaut est invisible en desktop et ne se reproduit que sous 1024px : une nouvelle
  // page doit en hériter, pas se souvenir de le retirer.
  //
  // `pageScrollClass` = le conteneur qui scrollait ; `pageHeightClass` = ses enfants qui se
  // contentaient d'étirer la hauteur.
  //
  // Un écran v2 sous chrome v2 les retire pour la même raison que le shell mobile, mais à
  // l'envers : là c'est la FENÊTRE qui scrolle (`.gd-app` est en `min-height`, pas `height`), et
  // c'est ce qui rend le `position: sticky` du header effectif. Une page qui garde
  // `h-full overflow-auto` rouvre un conteneur de scroll interne : le header ne bouge plus avec le
  // contenu et ne se colle plus.
  //
  // Le chrome v2 NE SUFFIT PAS : il habille aussi des écrans legacy (cf. useScreenVersion), qui
  // ont besoin de leur cadre de hauteur — c'est même la configuration de MEP (chrome v2 seul).
  pageScrollClass: ComputedRef<string>;
  pageHeightClass: ComputedRef<string>;
  // « Qui scrolle ? » exposé tel quel pour les pages qui portent d'autres classes de hauteur que
  // les deux ci-dessus (cf. `document-page-content` sur la page dossier).
  windowScrolls: ComputedRef<boolean>;
  // Réexposés pour que le layout tienne son format et son contrat de scroll d'une seule source.
  bp: ComputedRef<DsBreakpoint>;
  isMobileBp: ComputedRef<boolean>;
  // Classes de densité du kit, pour le conteneur qui enveloppe la page : l'amont les pose sur son
  // shell, et s'arrête à `lg` parce qu'il bascule là sur son propre arbre mobile.
  bpClasses: ComputedRef<string[]>;
}

export function useMobileShell (): MobileShell {
  const { bp, isMobileBp } = useDsBreakpoint();
  const { isV2 } = useFeatureVersion();
  const { chromeV2 } = useChromeVersion();
  const { screenV2 } = useScreenVersion();
  const isPopup = useIsPopup();

  const showMobileShell = computed(() => isMobileBp.value && isV2('mobile') && !isPopup.value);
  // La popup n'a ni shell mobile ni chrome v2 (layouts/default.vue rend sa branche en premier) :
  // elle garde son scroll de page.
  const windowScrolls = computed(() =>
    !isPopup.value && (showMobileShell.value || (chromeV2.value && screenV2.value)));
  const pageScrollClass = computed(() => (windowScrolls.value ? '' : 'h-full overflow-auto'));
  const pageHeightClass = computed(() => (windowScrolls.value ? '' : 'h-full'));
  const bpClasses = computed<string[]>(() =>
    (isMobileBp.value ? [`gd-bp-${bp.value}`, 'gd-bp-lg'] : [`gd-bp-${bp.value}`]));

  return { showMobileShell, pageScrollClass, pageHeightClass, windowScrolls, bp, isMobileBp, bpClasses };
}
